import React, { useState, useRef, useEffect } from 'react';
import { mcAPI, aiAPI, authAPI } from '../services/api';
import { isNativeApp } from '../services/mcNative';
import { useLang } from '../i18n/LangContext';

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/ipjjoaopeimoooklbnjjkeaojjhenaig';

// Mapowanie kategorii display format → MC categoryIds
// Rozszerzenie Chrome czyta r.categories — uzupełniamy je tutaj żeby nie czekać na aktualizację w sklepie
const MC_CATEGORY_IDS = {
  'sos': 222, 'sosy i dipy': 222,
  'zupa': 230, 'zupy': 230,
  'sałatka': 238, 'sałatki': 238,
  'dodatek': 246, 'dodatki': 246,
  'przekąska': 254, 'przekąski': 254,
  'obiad': 262, 'dania główne': 262,
  'dla niemowląt': 270,
  'napój': 278, 'napoje': 278,
  'one-pot': 286, 'all-in-one': 286,
  'deser': 310, 'desery': 310,
  'wypieki': 318, 'pieczywo': 318, 'ciasto': 318,
  'śniadanie': 326, 'śniadania': 326,
  'kolacja': 334, 'kolacje': 334,
  'przystawka': 342, 'przystawki': 342,
  'wegetariańskie': 586,
  'wegańskie': 577,
};
function resolveCategoryIds(category) {
  const id = MC_CATEGORY_IDS[(category || 'obiad').toLowerCase().trim()] ?? 262;
  return [id];
}

// Delikatny dźwięk powiadomienia (dwa ciche tony) — dla błędów wysyłki na MC,
// które mogą wyjść na jaw dopiero po dłuższej ciszy (użytkownik odszedł od ekranu).
// Web Audio API zamiast pliku audio — zero dodatkowych zasobów w bundlu.
function playNotifySound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [660, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = now + i * 0.12;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.07, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.3);
    });
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch {}
}

// Wyraźniejszy sygnał (trzy tony) dla bramki połączenia z MC — użytkownik ma zauważyć,
// że bez wtyczki/sesji wysyłka na MC nie zadziała. Przeglądarka pozwala na dźwięk dopiero
// po jakimkolwiek geście użytkownika na stronie; bez niego kontekst zostaje wstrzymany
// i zostaje sam modal.
function playAlertSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beep = () => {
      const now = ctx.currentTime;
      [523, 659, 784].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t0 = now + i * 0.14;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(0.12, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.35);
      });
      setTimeout(() => ctx.close().catch(() => {}), 900);
    };
    if (ctx.state === 'running') { beep(); return; }
    ctx.resume().then(() => { if (ctx.state === 'running') beep(); else ctx.close().catch(() => {}); })
      .catch(() => {});
  } catch {}
}

function RecipeConverter({ mcSession, extDetected, user, onUserUpdate, externalRecipe, onMcConnect }) {
  const { t, tErr, lang, changeLang } = useLang();
  const convertingMsgs = t('converting_msgs');

  const [prompt, setPrompt] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [ocrLoading, setOcrLoading] = useState(false);
  const ocrInputRef = useRef(null);
  // Osobny input z capture — w apce otwiera aparat zamiast galerii
  const cameraInputRef = useRef(null);
  const jsonImportInputRef = useRef(null);
  // Zdjęcie potrawy (miniatura na MC) — osobne od zdjęcia do OCR: tamto jest źródłem
  // tekstu przepisu, to trafia na konto MC jako thumbnail. dishMedia trzyma ID mediów
  // zwrócone przez MC, podpinane pod przepis dopiero po udanej wysyłce.
  const [dishPreview, setDishPreview] = useState('');
  const [dishMedia, setDishMedia] = useState(null);
  const [dishUploading, setDishUploading] = useState(false);
  const [dishError, setDishError] = useState('');
  const dishInputRef = useRef(null);
  // Wysyłka w tle przeżywa zamknięcie karty — trzymamy jej ślad w localStorage,
  // żeby po powrocie na stronę dowiedzieć się o wyniku i pokazać link do przepisu.
  const [bgJob, setBgJob] = useState(null);
  const [converting, setConverting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [warnings, setWarnings] = useState(null);
  // Raport audytu po konwersji: { fixed: [...], attention: [...], ai_used: bool }.
  // `attention` jest już wtopione w `warnings` przez backend — tutaj pokazujemy `fixed`,
  // czyli co zostało poprawione automatycznie, żeby nie działo się to po cichu.
  const [audit, setAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [error, setError] = useState('');
  const [pushSuccess, setPushSuccess] = useState('');
  // Trwa dopytywanie MC czy przepis powstał mimo timeoutu wtyczki — osobno od `pushing`,
  // bo komunikat i etykieta przycisku muszą mówić co innego niż przy zwykłej wysyłce
  const [pushChecking, setPushChecking] = useState(false);
  // Blokada wielokrotnego wysłania: pushing = trwa wysyłka, pushedResult = obiekt
  // przepisu już wysłanego (każda edycja tworzy nowy obiekt, więc porównanie
  // referencji samo odblokowuje przycisk po zmianie przepisu)
  const [pushing, setPushing] = useState(false);
  const [pushedResult, setPushedResult] = useState(null);
  const [pushedUrl, setPushedUrl] = useState(null);
  const pushingRef = useRef(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [copiedJSON, setCopiedJSON] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showExtModal, setShowExtModal] = useState(false);
  const [cookieBanner, setCookieBanner] = useState(!localStorage.getItem('cookie-accepted'));
  const [copyrightBanner, setCopyrightBanner] = useState(!localStorage.getItem('copyright-accepted'));
  const [extBannerDismissed, setExtBannerDismissed] = useState(!!localStorage.getItem('ext_banner_dismissed'));
  // Banner rozszerzenia wjeżdża z opóźnieniem — przy pierwszym wejściu konkuruje
  // z "Zalogowano!", notką cookies i ostrzeżeniem AI (przegląd UI 2026-06-12)
  const [extBannerReady, setExtBannerReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setExtBannerReady(true), 12000);
    return () => clearTimeout(timer);
  }, []);
  const [selectedSteps, setSelectedSteps] = useState(new Set());
  const [fixing, setFixing] = useState(false);
  const [fixNotes, setFixNotes] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [repairInstruction, setRepairInstruction] = useState('');
  const [repairQuestion, setRepairQuestion] = useState('');
  const [repairAnswer, setRepairAnswer] = useState('');
  const [repairHistory, setRepairHistory] = useState([]);
  const [repairSavePrompt, setRepairSavePrompt] = useState(false);
  const [currentSavedId, setCurrentSavedId] = useState(null);
  const [mcPayload, setMcPayload] = useState(null);
  const [funMsgIdx, setFunMsgIdx] = useState(0);
  const [funMsgVisible, setFunMsgVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [stageMsg, setStageMsg] = useState('');
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [savedPreview, setSavedPreview] = useState(null);
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('converter');
  const [usage, setUsage] = useState(null);
  // Wysyłka wsadowa z zakładki "Zapisane" — kolejka po jednym przepisie na raz
  // (jedna sesja MC, nie da się równolegle), z paskiem postępu i podsumowaniem błędów.
  const [selectedSaved, setSelectedSaved] = useState(new Set());
  const [bulkPush, setBulkPush] = useState(null); // {running, total, current, currentTitle, failed:[]}
  const bulkCancelRef = useRef(false);
  // Ściąganie zdjęcia z już opublikowanego przepisu na MC (per przepis, ręcznie)
  const [photoFetch, setPhotoFetch] = useState({}); // {[id]: 'loading'|'done'|'error'}
  // Wsadowe ściąganie zdjęć zaznaczonych przepisów — rób to PRZED skasowaniem
  // przepisów z konta MC, bo po skasowaniu zdjęć nie da się już pobrać
  const [bulkPhoto, setBulkPhoto] = useState(null); // {running, total, current, currentTitle, failed:[]}
  const bulkPhotoCancelRef = useRef(false);
  // Eksport/import paczek (wiele przepisów naraz) z zakładki "Zapisane"
  const jsonPackImportInputRef = useRef(null);
  const [packBusy, setPackBusy] = useState(false);
  const [packMsg, setPackMsg] = useState('');

  const resultRef = useRef(null);
  const scrollPendingRef = useRef(false);

  // Scroll to result when it appears in DOM (handles tab-switch delay)
  useEffect(() => {
    if (result && activeTab === 'converter' && scrollPendingRef.current) {
      scrollPendingRef.current = false;
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, [result, activeTab]);

  const loadSavedRecipes = async () => {
    try {
      const res = await aiAPI.getSavedRecipes();
      const list = res.data;
      setSavedRecipes(Array.isArray(list) ? list : []);
    } catch {}
  };

  const loadUsage = async () => {
    try {
      const r = await aiAPI.getConversionStatus();
      setUsage(r.data);
    } catch {}
  };

  useEffect(() => { loadSavedRecipes(); loadUsage(); }, []);

  // Wznowienie śledzenia wysyłki po przeładowaniu strony. Serwer i tak doprowadzi
  // przepis do końca, ale link musi trafić do użytkownika i do przepiśnika —
  // bez tego zamknięcie karty gubiło wynik gotowej wysyłki.
  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem('mc_push_job') || 'null'); } catch {}
    if (!stored?.jobId) return;
    let cancelled = false;
    setBgJob({ ...stored, status: 'waiting' });

    (async () => {
      const until = Date.now() + 600000;
      while (!cancelled && Date.now() < until) {
        try {
          const st = (await aiAPI.mcPushStatus(stored.jobId))?.data;
          if (st?.status === 'done') {
            localStorage.removeItem('mc_push_job');
            if (!cancelled) setBgJob({ ...stored, status: 'done', url: st.url });
            if (stored.savedId) {
              try {
                await aiAPI.updateSavedRecipe(stored.savedId, {
                  mc_url: st.url, mc_recipe_id: String(st.recipeId || ''),
                });
                loadSavedRecipes();
              } catch {}
            }
            return;
          }
          if (st?.status === 'error' || st?.status === 'unknown') {
            localStorage.removeItem('mc_push_job');
            if (!cancelled) {
              setBgJob(null);
              if (st?.error === 'session_expired') {
                setError(t('conv.error_mc_session_expired'));
                playNotifySound();
              }
            }
            return;
          }
        } catch {}
        await new Promise((r) => setTimeout(r, 3000));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Sync app language → mc_locale preference in DB (merge — nie nadpisuje mc_session)
  useEffect(() => {
    if (!user) return;
    authAPI.updateProfile({ preferences: { mc_locale: lang } }).catch(() => {});
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!converting) return;
    setFunMsgIdx(Math.floor(Math.random() * convertingMsgs.length));
    setFunMsgVisible(true);
    const interval = setInterval(() => {
      setFunMsgVisible(false);
      setTimeout(() => {
        setFunMsgIdx(i => (i + 1) % convertingMsgs.length);
        setFunMsgVisible(true);
      }, 400);
    }, 6500);
    return () => clearInterval(interval);
  }, [converting]);

  const handleConvert = async (opts) => {
    if (!prompt.trim()) { setError(t('conv.error_empty')); return; }
    // Bez wtyczki/sesji MC przepisu i tak się nie wyśle — ostrzegamy przed spaleniem konwersji.
    // Raz na załadowanie strony; "Konwertuj mimo to" w modalu wraca tu z opts.skipGate.
    if (connectNeed && !opts?.skipGate && !gateConvertShownRef.current) {
      gateConvertShownRef.current = true;
      openConnectGate();
      return;
    }
    setConverting(true); setError(''); setResult(null); setPushSuccess(''); setSaveSuccess('');
    setWarnings(null); setAudit(null); setAuditOpen(false);
    setCurrentSavedId(null); setRepairSavePrompt(false);
    setProgress(0);

    try {
      const res = await mcAPI.convertRecipe(
        { text: prompt, lang },
        (event) => {
          if (typeof event.progress === 'number') setProgress(event.progress);
          if (event.message) setStageMsg(event.message);
        }
      );
      setProgress(100);
      if (res.success) {
        setResult(res.recipe);
        setMcPayload(res.mc_payload || null);
        setWarnings(res.warnings?.length > 0 ? res.warnings : null);
        setAudit(res.audit?.fixed?.length > 0 ? res.audit : null);
        setAuditOpen(false);
        if (res.usage) setUsage(res.usage);
        setEditing(false);
        scrollPendingRef.current = true;
        handleSave(res.recipe, res.mc_payload || null);
      } else {
        setError(tErr(res.error) || t('conv.error_conv'));
        if (res.limit_reached) loadUsage();
      }
    } catch (err) {
      setError(tErr(err.message) || t('conv.error_net'));
    } finally {
      setConverting(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  const handleImportUrl = async () => {
    const url = importUrl.trim();
    if (!url || importing) return;
    setImporting(true); setImportMsg(''); setError('');
    try {
      const res = await aiAPI.importUrl(url);
      if (res?.data?.text) {
        setPrompt(res.data.text);
        setImportMsg(t('conv.url_ok'));
        setTimeout(() => setImportMsg(''), 6000);
      } else {
        setError(t('conv.url_error'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('conv.url_error'));
    } finally { setImporting(false); }
  };

  const handleOcrFile = async (file) => {
    if (!file || ocrLoading || converting) return;
    if (!file.type.startsWith('image/')) { setError(t('conv.image_err')); return; }
    setOcrLoading(true); setImportMsg(''); setError('');
    try {
      const res = await aiAPI.ocrImage(file);
      if (res?.data?.text) {
        setPrompt(prev => (prev.trim() ? prev.trim() + '\n\n' : '') + res.data.text);
        setImportMsg(t('conv.image_ok'));
        setTimeout(() => setImportMsg(''), 6000);
      } else {
        setError(t('conv.image_err'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('conv.image_err'));
    } finally { setOcrLoading(false); }
  };

  // Zdjęcie potrawy leci na MC od razu po wybraniu — dzięki temu w chwili wysyłki
  // przepisu miniatura jest już gotowa i nie wydłuża i tak wolnego POST-a.
  const handleDishFile = async (file) => {
    if (!file || dishUploading) return;
    if (!file.type.startsWith('image/')) { setDishError(t('conv.dish_err')); return; }
    setDishUploading(true); setDishError('');
    const localPreview = URL.createObjectURL(file);
    try {
      const res = await aiAPI.uploadMcImage(file, lang);
      if (res?.data?.success) {
        setDishMedia({
          detailsMediaId: res.data.detailsMediaId,
          thumbnailMediaId: res.data.thumbnailMediaId,
        });
        setDishPreview(localPreview);
      } else {
        setDishError(res?.data?.error || t('conv.dish_err'));
      }
    } catch (err) {
      setDishError(err.response?.data?.error || t('conv.dish_err'));
    } finally {
      setDishUploading(false);
      if (dishInputRef.current) dishInputRef.current.value = '';
    }
  };

  const clearDishImage = () => {
    setDishMedia(null); setDishPreview(''); setDishError('');
    if (dishInputRef.current) dishInputRef.current.value = '';
  };

  const handleImagePaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'));
    if (item) { e.preventDefault(); handleOcrFile(item.getAsFile()); }
  };

  const updateField = (field, value) => {
    setMcPayload(null); setDirty(true);
    setResult(prev => ({ ...prev, [field]: value }));
  };

  const updateIngredient = (idx, field, value) => {
    setMcPayload(null); setDirty(true);
    setResult(prev => {
      const ings = [...prev.ingredients];
      ings[idx] = { ...ings[idx], [field]: value };
      return { ...prev, ingredients: ings };
    });
  };

  const addIngredient = () => {
    setMcPayload(null); setDirty(true);
    setResult(prev => ({
      ...prev,
      ingredients: [...(prev.ingredients || []), { name: '', quantity: '' }],
    }));
  };

  const removeIngredient = (idx) => {
    setMcPayload(null); setDirty(true);
    setResult(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== idx),
    }));
  };

  const updateStep = (idx, field, value) => {
    setMcPayload(null); setDirty(true);
    setResult(prev => {
      const steps = [...prev.steps];
      steps[idx] = { ...steps[idx], [field]: value };
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    setMcPayload(null); setDirty(true);
    setResult(prev => ({
      ...prev,
      steps: [...(prev.steps || []), { type: 'manual', title: '', description: '', speed: null, temperature: null, time_seconds: null }],
    }));
  };

  const removeStep = (idx) => {
    setMcPayload(null); setDirty(true);
    setResult(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx),
    }));
  };

  const moveStep = (idx, dir) => {
    setMcPayload(null); setDirty(true);
    setResult(prev => {
      const steps = [...prev.steps];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= steps.length) return prev;
      [steps[idx], steps[newIdx]] = [steps[newIdx], steps[idx]];
      return { ...prev, steps };
    });
  };

  const handlePush = async () => {
    // pushingRef chroni przed podwójnym kliknięciem zanim React zdąży przerenderować
    if (!result || pushingRef.current || result === pushedResult) return;
    pushingRef.current = true;
    setPushing(true);
    setPushSuccess(''); setError('');
    const recipe = result;
    const startedAt = Date.now();
    const finishPush = () => { pushingRef.current = false; setPushing(false); };

    // Payload do wysyłki: świeży z konwersji, a po edycji przebudowany przez backend
    // (/ai/mc-rebuild-payload zachowuje tryby dedykowane dzięki ukrytemu mc_mode w krokach).
    // Fallback gdy rebuild padnie: display recipe — wtyczka zbuduje payload sama
    // (tryby dedykowane degradują się wtedy do customized).
    let payloadToSend = (mcPayload && mcPayload.servingSizes) ? mcPayload : null;
    if (!payloadToSend) {
      try {
        const res = await aiAPI.rebuildMcPayload(recipe, lang);
        if (res?.data?.mc_payload?.servingSizes) {
          payloadToSend = res.data.mc_payload;
          setMcPayload(payloadToSend);
        }
      } catch {}
    }

    // Gdy wysyłkę poprowadził serwer, miniatura poszła razem z publikacją —
    // powtórny PUT z przeglądarki byłby tylko zbędnym żądaniem
    let mediaDoneByServer = false;

    // Wspólne zakończenie sukcesem — używane zarówno gdy wtyczka odda wynik,
    // jak i gdy ID przepisu odzyskamy z MC po jej błędzie
    const applyPushSuccess = async (recipeId, extUrl, message) => {
      setPushedResult(recipe);
      // Link do edytora przepisu (create-recipe), nie do listy moich przepisów —
      // wtyczka zwraca w extUrl ten drugi, więc budujemy URL sami z recipeId
      const url = recipeId
        ? `https://www.monsieur-cuisine.com/${lang}/create-recipe?recipe-id=${recipeId}`
        : extUrl;
      // Przycisk "Wyślij na MC" zamienia się w "Otwórz przepis na MC" (render w actions),
      // tu zostaje tylko krótki komunikat
      setPushedUrl(url || null);
      setError('');
      setPushSuccess(message || (url ? t('conv.sent_excl') : t('conv.saved_mc')));
      // Miniatura idzie osobnym PUT-em dopiero teraz, gdy przepis na pewno istnieje.
      // Cicho na porażkę: przepis jest już na koncie, brak zdjęcia nie jest błędem
      // wysyłki i nie ma prawa zamienić sukcesu w komunikat o błędzie.
      if (recipeId && dishMedia && !mediaDoneByServer) {
        try {
          await aiAPI.attachMcImage(recipeId, payloadToSend || mcPayload, dishMedia, lang);
        } catch {}
      }
      try {
        const savePayload = {
          title: recipe.title,
          recipe: { ...recipe, _mc_payload: payloadToSend || mcPayload, mc_url: url, mc_recipe_id: recipeId },
          mc_url: url,
          mc_recipe_id: String(recipeId || ''),
        };
        // Bez duplikatu w przepiśniku: gdy przepis ma już zapis, nadpisz go wersją wysłaną
        if (currentSavedId) {
          await aiAPI.updateSavedRecipe(currentSavedId, savePayload);
        } else {
          const res = await aiAPI.saveRecipe(savePayload);
          setCurrentSavedId(res?.data?.id || null);
        }
        loadSavedRecipes();
      } catch {}
    };

    // ── Wyścig: wtyczka vs. wyszukiwanie przepisu na MC ──────────────────────
    // Wtyczka nie dostaje od MC odpowiedzi z ID (POST create dostaje 502 po 30 s po
    // stronie MC), więc czekanie na nią to czysta strata czasu. Dlatego szukamy
    // przepisu na koncie MC RÓWNOLEGLE z wysyłką, od pierwszych sekund. Kto pierwszy
    // ma ID — wtyczka czy nasze odpytywanie — ten pokazuje link.
    // Offsety w ms od startu wysyłki: gęsto na początku (gdy przepis pojawia się
    // od razu), potem coraz rzadziej, do 5 minut.
    const RECOVER_AT = [];
    for (let ms = 2000;  ms <=  30000; ms += 2000)  RECOVER_AT.push(ms);
    for (let ms = 35000; ms <=  90000; ms += 5000)  RECOVER_AT.push(ms);
    for (let ms = 100000; ms <= 300000; ms += 10000) RECOVER_AT.push(ms);

    // Wynik bierze tylko jedna ścieżka — druga musi się wycofać bez ruszania UI
    let settled = false;
    const claim = () => (settled ? false : (settled = true));

    const succeed = async (recipeId, extUrl, message) => {
      if (!claim()) return;
      clearTimeout(deadline);
      document.removeEventListener('MC_SEND_RECIPE_RESULT', onResult);
      await applyPushSuccess(recipeId, extUrl, message);
      setPushChecking(false);
      finishPush();
    };

    const fail = (message) => {
      if (!claim()) return;
      clearTimeout(deadline);
      document.removeEventListener('MC_SEND_RECIPE_RESULT', onResult);
      setPushChecking(false);
      setError(message);
      playNotifySound();
      finishPush();
    };

    const pollForRecipe = async () => {
      for (const at of RECOVER_AT) {
        const wait = at - (Date.now() - startedAt);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
        if (settled) return;
        // Okno szukania = czas od startu wysyłki + minuta zapasu, żeby nie podłapać
        // starszej kopii tego samego przepisu sprzed godziny
        const within = Math.max(2, Math.ceil((Date.now() - startedAt) / 60000) + 1);
        try {
          const res = await aiAPI.recoverMcPush(recipe.title, payloadToSend || mcPayload, lang, within);
          if (res?.data?.found && res.data.recipeId) {
            const msg = res.data.published === false ? t('conv.sent_draft') : t('conv.sent_excl');
            await succeed(res.data.recipeId, res.data.url, msg);
            return;
          }
        } catch {}
      }
      fail(t('conv.error_ext'));
    };

    // Ostateczny bezpiecznik — odpytywanie kończy się samo, to tylko siatka pod nim
    const deadline = setTimeout(() => fail(t('conv.error_ext')), 320000);

    const onResult = (e) => {
      if (e.detail?.success) {
        succeed(e.detail.recipeId, e.detail.url);
        return;
      }
      // Błąd wtyczki NIE kończy wysyłki — przepis zwykle i tak powstaje na MC,
      // więc oddajemy sprawę odpytywaniu i zmieniamy tylko komunikat
      if (!settled) setPushChecking('ext');
    };
    // ── Ścieżka główna: wysyłka w tle po stronie serwera ─────────────────────
    // MC i tak potrzebuje ~2 minut na utworzenie przepisu, a odpowiedź POST-a nigdy
    // nie przynosi ID (MC zrywa połączenie po 30 s). Serwer robi to za nas i pilnuje
    // sprawy do końca — użytkownik odzyskuje stronę od razu i może ją zamknąć.
    if (payloadToSend) {
      try {
        const res = await aiAPI.pushMcAsync(payloadToSend, lang, dishMedia);
        const jobId = res?.data?.job_id;
        if (jobId) {
          mediaDoneByServer = !!dishMedia;
          // 'server' = inny komunikat: tu wolno zamknąć stronę, wtyczka nie brała udziału
          setPushChecking('server');
          // Ślad przeżyje zamknięcie karty — po powrocie odbierzemy link
          try {
            localStorage.setItem('mc_push_job', JSON.stringify({
              jobId, title: recipe.title, savedId: currentSavedId,
            }));
          } catch {}
          const untilMs = Date.now() + 480000;
          const watchJob = async () => {
            while (Date.now() < untilMs && !settled) {
              await new Promise((r) => setTimeout(r, 2000));
              if (settled) return;
              try {
                const st = (await aiAPI.mcPushStatus(jobId))?.data;
                if (st?.status === 'done') {
                  try { localStorage.removeItem('mc_push_job'); } catch {}
                  const msg = st.published === false ? t('conv.sent_draft') : t('conv.sent_excl');
                  await succeed(st.recipeId, st.url, msg);
                  return;
                }
                if (st?.status === 'error') {
                  try { localStorage.removeItem('mc_push_job'); } catch {}
                  if (st.error === 'session_expired') {
                    fail(t('conv.error_mc_session_expired'));
                    return;
                  }
                  break;
                }
              } catch { /* chwilowy błąd odpytania — próbujemy dalej */ }
            }
            // Serwer nie dowiózł — oddajemy sprawę wtyczce i odpytywaniu jak dawniej,
            // ale tylko gdy wtyczka/apka faktycznie odpowiada na pingi (extDetected)
            if (!settled) {
              if (!extDetected) {
                fail(t('conv.error_no_extension'));
                openConnectGate(false); // dźwięk gra już w fail()
              } else {
                setPushChecking('ext');
                document.addEventListener('MC_SEND_RECIPE_RESULT', onResult);
                pollForRecipe();
                document.dispatchEvent(new CustomEvent('MC_SEND_RECIPE',
                  { detail: { recipe: payloadToSend, lang } }));
              }
            }
          };
          watchJob();
          return;
        }
      } catch { /* brak sesji / Redisa — lecimy starą ścieżką przez wtyczkę */ }
    }

    // Bez serwerowej ścieżki jedyna nadzieja to wtyczka/apka — jeśli nigdy nie
    // odpowiedziała na ping (App.jsx), nie ma sensu czekać 5 minut w ciszy
    if (!extDetected) {
      fail(t('conv.error_no_extension'));
      openConnectGate(false);
      return;
    }
    document.addEventListener('MC_SEND_RECIPE_RESULT', onResult);
    pollForRecipe();
    // Gotowy payload — rozszerzenie wykrywa go po polu servingSizes i wysyła bez przebudowy
    if (payloadToSend) {
      document.dispatchEvent(new CustomEvent('MC_SEND_RECIPE', { detail: { recipe: payloadToSend, lang } }));
      return;
    }
    // Dodaj categories żeby rozszerzenie Chrome wybrało właściwy ID (r.categories || [262])
    const recipeWithCategory = { ...recipe, categories: resolveCategoryIds(recipe.category) };
    document.dispatchEvent(new CustomEvent('MC_SEND_RECIPE', { detail: { recipe: recipeWithCategory, lang } }));
  };

  const handleSave = async (recipeOverride, payloadOverride, replaceId = null) => {
    const recipeData = recipeOverride || result;
    const payloadData = payloadOverride !== undefined ? payloadOverride : mcPayload;
    if (!recipeData) return;
    setSaving(true); setSaveSuccess('');
    try {
      const payload = {
        title: recipeData.title,
        recipe: { ...recipeData, _mc_payload: payloadData },
      };
      // replaceId = stary zapis do usunięcia (wywołanie z repair-save-prompt)
      if (replaceId) {
        await aiAPI.deleteSavedRecipe(replaceId);
        const res = await aiAPI.saveRecipe(payload);
        setCurrentSavedId(res?.data?.id || null);
      } else if (currentSavedId) {
        // Przepis już zapisany — aktualizuj zamiast tworzyć duplikat
        await aiAPI.updateSavedRecipe(currentSavedId, payload);
      } else {
        // Pierwszy zapis
        const res = await aiAPI.saveRecipe(payload);
        setCurrentSavedId(res?.data?.id || null);
      }
      setSaveSuccess(t('conv.save_ok'));
      setTimeout(() => setSaveSuccess(''), 4000);
      loadSavedRecipes();
    } catch (err) {
      setError(tErr(err.response?.data?.error) || t('conv.error_save'));
    } finally { setSaving(false); }
  };

  // Przepis wczytany przez admina z panelu (cudzy) — id zerowane, żeby
  // "Zapisz" utworzył kopię na koncie admina zamiast PUT-ować cudzy rekord
  useEffect(() => {
    if (externalRecipe) handleLoadSaved({ ...externalRecipe, id: null });
  }, [externalRecipe]);

  const handleLoadSaved = (saved) => {
    setSavedPreview(null);
    try {
      const raw = saved.recipe || (typeof saved.recipe_data === 'string'
        ? JSON.parse(saved.recipe_data)
        : saved.recipe_data);
      const { _mc_payload, ...recipe } = raw;
      setResult(recipe);
      setMcPayload(_mc_payload || null);
      setCurrentSavedId(saved.id || null);
      setRepairSavePrompt(false);
      setEditing(false);
      setPrompt('');
      setError(''); setPushSuccess(''); setSaveSuccess('');
      // Ostrzeżenia i audyt dotyczyły POPRZEDNIO konwertowanego przepisu —
      // przy wczytaniu innego z biblioteki wprowadzałyby w błąd.
      setWarnings(null); setAudit(null); setAuditOpen(false);
      setSelectedSteps(new Set());
      clearDishImage();
      scrollPendingRef.current = true;
      setActiveTab('converter');
    } catch { setError(t('conv.error_load')); }
  };

  // Konwersje blob<->base64 do (od)dawania zdjęcia jako część pliku eksportu —
  // JSON nie niesie binarnych bajtów, więc zdjęcie jedzie jako data_base64.
  const _blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result || '';
      const comma = dataUrl.indexOf(',');
      resolve({ mime: blob.type || 'image/jpeg', data_base64: comma >= 0 ? dataUrl.slice(comma + 1) : '' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const _b64ToBlob = (b64, mime) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime || 'image/jpeg' });
  };

  const _slugifyTitle = (title) => (title || 'przepis')
    .normalize('NFKD').replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'przepis';

  // Format eksportu ".mtmeal": JSON scrambled przez XOR z wbudowanym kluczem, potem
  // base64, z sygnaturą na początku. NIE jest to szyfrowanie — klucz jeździ w tym samym
  // pliku JS co reszta appki i ktoś techniczny z devtoolsami go znajdzie. Cel jest węższy:
  // zwykły użytkownik otwierający plik w Notatniku/przeglądarce widzi bezsensowny ciąg
  // znaków zamiast czytelnego JSON-a z przepisem — nie da się przypadkiem podejrzeć ani
  // skopiować treści bez zaimportowania z powrotem do matemeal.
  const _MTMEAL_MAGIC = 'MATEMEAL-RECIPE-V1';
  const _MTMEAL_KEY = 'matemeal-2026-recipe-lock';

  const _xorBytes = (bytes, key) => {
    const keyBytes = new TextEncoder().encode(key);
    const out = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) out[i] = bytes[i] ^ keyBytes[i % keyBytes.length];
    return out;
  };

  const _bytesToBase64 = (bytes) => {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  };

  const _base64ToBytesLocal = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  const _encodeMatemealFile = (data) => {
    const jsonBytes = new TextEncoder().encode(JSON.stringify(data));
    const scrambled = _xorBytes(jsonBytes, _MTMEAL_KEY);
    return `${_MTMEAL_MAGIC}\n${_bytesToBase64(scrambled)}`;
  };

  // XOR jest symetryczny — samo odszyfrowanie to ta sama operacja co szyfrowanie.
  // Zwraca null (nie rzuca) gdy plik nie ma naszej sygnatury, żeby wywołujący mógł
  // spróbować starego, jawnego JSON-a (wsteczna kompatybilność z eksportami sprzed tej zmiany).
  const _decodeMatemealFile = (text) => {
    const nl = text.indexOf('\n');
    if (nl < 0 || text.slice(0, nl).trim() !== _MTMEAL_MAGIC) return null;
    try {
      const scrambled = _base64ToBytesLocal(text.slice(nl + 1).trim());
      const jsonBytes = _xorBytes(scrambled, _MTMEAL_KEY);
      return JSON.parse(new TextDecoder().decode(jsonBytes));
    } catch {
      return null;
    }
  };

  // Akceptuje zarówno nowy format .mtmeal jak i stare, jawne eksporty .json.
  const _parseMatemealFileText = (text) => _decodeMatemealFile(text) ?? JSON.parse(text);

  // W apce Android (WebView Capacitor) <a download> na blob URL jest martwym
  // klikiem — WebView nie ma zarejestrowanego mechanizmu pobierania jak prawdziwa
  // przeglądarka, więc nic się nie dzieje (znane ograniczenie, nie bug tej appki).
  // Tam zapisujemy plik natywnie (plugin Filesystem) i otwieramy systemowe okno
  // "Udostępnij" (plugin Share) — użytkownik może zapisać/wysłać plik dalej.
  const _downloadMatemealFile = async (data, filename) => {
    const content = _encodeMatemealFile(data);
    if (isNativeApp()) {
      try {
        const plugins = window.Capacitor?.Plugins || {};
        await plugins.Filesystem.writeFile({ path: filename, data: btoa(content), directory: 'CACHE' });
        const { uri } = await plugins.Filesystem.getUri({ path: filename, directory: 'CACHE' });
        await plugins.Share.share({ title: filename, url: uri });
      } catch {
        setError(t('conv.export_native_err'));
      }
      return;
    }
    const blob = new Blob([content], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Eksport pojedynczego przepisu do pliku .mtmeal — do udostępniania i wgrania
  // z powrotem przez handleImportJSONFile. Treść zakodowana (zob. _encodeMatemealFile
  // wyżej) — nie jest to już czytelny JSON do wklejenia w czacie AI ani ręcznej edycji.
  // Emoji w nazwie pliku (📖) to jedyny sposób na "ikonkę" bez rejestrowania własnego
  // typu pliku w systemie — w Finderze/Explorerze wyświetla się w nazwie.
  const exportRecipeJSON = async () => {
    if (!result) return;
    let photo = null;
    try {
      // Świeżo wybrane zdjęcie potrawy (jeszcze przed wysyłką) — bajty żyją
      // w lokalnym blob URL, dishPreview. Inaczej: zdjęcie już zapisane na
      // serwerze przy tym przepisie (_dish_photo_saved) — ściągnij jako blob.
      if (dishPreview) {
        const blob = await (await fetch(dishPreview)).blob();
        photo = await _blobToBase64(blob);
      } else if (result._dish_photo_saved && currentSavedId) {
        const res = await aiAPI.getDishPhoto(currentSavedId);
        photo = await _blobToBase64(res.data);
      }
    } catch {}
    const envelope = {
      matemeal_export: true,
      version: 1,
      type: 'recipe',
      exported_at: new Date().toISOString(),
      app: 'matemeal.com',
      recipe: { ...result, _mc_payload: mcPayload || undefined },
      ...(photo ? { photo } : {}),
    };
    await _downloadMatemealFile(envelope, `📖 ${_slugifyTitle(result.title)}.mtmeal`);
  };

  // Import — bezpośredni, bez AI: plik musi już mieć strukturę matemeal/MC
  // (steps z type/mode itd.), taką samą jak przepis wczytany z przepiśnika.
  // Rozpoznaje też paczkę (wiele przepisów, zob. handleExportPack) i przekierowuje
  // ją do importRecipePack zamiast ładować do edytora pojedynczego przepisu.
  const handleImportJSONFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = _parseMatemealFileText(reader.result);

        if (Array.isArray(parsed?.recipes)) {
          await importRecipePack(parsed.recipes);
          return;
        }

        const candidate = (parsed?.recipe?.title && Array.isArray(parsed.recipe.steps))
          ? parsed.recipe
          : (parsed?.title && Array.isArray(parsed.steps) ? parsed : null);
        if (!candidate) { setError(t('conv.import_json_err')); return; }
        handleLoadSaved({ id: null, recipe: candidate });

        // Zdjęcie z eksportu — ID mediów MC są przypisane do konta, więc jedyny sposób
        // "przeniesienia" zdjęcia to wgranie go od nowa jako świeże medium na koncie
        // odbiorcy (wymaga podłączonej sesji MC — bez niej po prostu pomijamy zdjęcie).
        const photo = parsed?.photo || parsed?.recipe?.photo;
        if (photo?.data_base64 && mcSession) {
          try {
            const blob = _b64ToBlob(photo.data_base64, photo.mime);
            const localPreview = URL.createObjectURL(blob);
            const uploadFile = new File([blob], 'imported-dish.jpg', { type: photo.mime || 'image/jpeg' });
            const res = await aiAPI.uploadMcImage(uploadFile, lang);
            if (res?.data?.success) {
              setDishMedia({ detailsMediaId: res.data.detailsMediaId, thumbnailMediaId: res.data.thumbnailMediaId });
              setDishPreview(localPreview);
            }
          } catch {}
        }

        setImportMsg(t('conv.import_json_ok'));
        setTimeout(() => setImportMsg(''), 6000);
      } catch {
        setError(t('conv.import_json_err'));
      }
    };
    reader.onerror = () => setError(t('conv.import_json_err'));
    reader.readAsText(file);
  };

  // Eksport zaznaczonych w przepiśniku przepisów jako jedna paczka .json —
  // do rozsyłania większych zestawów (np. "wszystkie przepisy na obiady").
  const handleExportPack = async () => {
    const queue = savedRecipes.filter(r => selectedSaved.has(r.id));
    if (!queue.length || packBusy) return;
    setPackBusy(true);
    try {
      const recipes = [];
      for (const r of queue) {
        const raw = r.recipe || (typeof r.recipe_data === 'string' ? JSON.parse(r.recipe_data) : r.recipe_data);
        let photo = null;
        if (raw?._dish_photo_saved) {
          try {
            const res = await aiAPI.getDishPhoto(r.id);
            photo = await _blobToBase64(res.data);
          } catch {}
        }
        recipes.push({ title: r.title, recipe: raw, ...(photo ? { photo } : {}) });
      }
      const envelope = {
        matemeal_export: true,
        version: 1,
        type: 'recipe-pack',
        exported_at: new Date().toISOString(),
        app: 'matemeal.com',
        count: recipes.length,
        recipes,
      };
      const stamp = new Date().toISOString().slice(0, 10);
      await _downloadMatemealFile(envelope, `📖 paczka-${recipes.length}-przepisow-${stamp}.mtmeal`);
    } finally {
      setPackBusy(false);
    }
  };

  // Import paczki — zapisuje przepisy WPROST do przepiśnika (bez edytora), gotowe
  // do zaznaczenia i wysyłki jednym przyciskiem "Wyślij do MC" (handleBulkPush).
  const importRecipePack = async (items) => {
    const clean = (items || [])
      .map(it => ({ title: it?.title || it?.recipe?.title, recipe: it?.recipe, photo: it?.photo }))
      .filter(it => it.title && it.recipe && Array.isArray(it.recipe.steps));
    if (!clean.length) { setError(t('conv.import_json_err')); return; }
    setPackBusy(true); setError('');
    try {
      const res = await aiAPI.saveRecipesBulk(clean);
      const saved = res?.data?.saved || 0;
      const skipped = res?.data?.skipped?.length || 0;
      setPackMsg(`${t('saved.pack_import_ok')}: ${saved}${skipped ? ` (${skipped} ${t('saved.pack_import_skipped')})` : ''}`);
      setTimeout(() => setPackMsg(''), 8000);
      setActiveTab('saved');
      loadSavedRecipes();
    } catch (err) {
      setError(err.response?.data?.error || t('conv.import_json_err'));
    } finally {
      setPackBusy(false);
    }
  };

  const handleImportPackFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = _parseMatemealFileText(reader.result);
        const items = Array.isArray(parsed?.recipes) ? parsed.recipes
          : (parsed?.recipe?.title ? [parsed] : (parsed?.title ? [{ title: parsed.title, recipe: parsed, photo: parsed.photo }] : null));
        if (!items) { setError(t('conv.import_json_err')); return; }
        await importRecipePack(items);
      } catch {
        setError(t('conv.import_json_err'));
      }
    };
    reader.onerror = () => setError(t('conv.import_json_err'));
    reader.readAsText(file);
  };

  // Miniaturka w oknie "Podgląd" — zdjęcie żyje na dysku serwera, <img src> nie
  // niósłby nagłówka JWT, więc pobieramy jako blob i budujemy lokalny object URL.
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setPreviewPhotoUrl(null);

    const recipe = savedPreview
      ? (savedPreview.recipe || (typeof savedPreview.recipe_data === 'string'
          ? (() => { try { return JSON.parse(savedPreview.recipe_data); } catch { return null; } })()
          : savedPreview.recipe_data))
      : null;

    if (savedPreview?.id && recipe?._dish_photo_saved) {
      aiAPI.getDishPhoto(savedPreview.id).then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPreviewPhotoUrl(objectUrl);
      }).catch(() => {});
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [savedPreview]);

  const handleDeleteSaved = async (id, e) => {
    e.stopPropagation();
    try {
      const { aiAPI } = await import('../services/api');
      await aiAPI.deleteSavedRecipe(id);
      setSavedRecipes(prev => prev.filter(r => r.id !== id));
      if (savedPreview?.id === id) setSavedPreview(null);
    } catch {}
  };

  const toggleSavedSelect = (id) => {
    setSelectedSaved(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllSaved = () => {
    setSelectedSaved(prev =>
      prev.size === savedRecipes.length ? new Set() : new Set(savedRecipes.map(r => r.id))
    );
  };

  // Wysyła po kolei zaznaczone przepisy na WŁASNE konto MC (sesja bieżącego usera).
  // Sekwencyjnie — jedna sesja MC nie obsłuży równoległych wysyłek. Błąd pojedynczego
  // przepisu nie przerywa kolejki, trafia do podsumowania na końcu.
  const handleBulkPush = async () => {
    if (bulkPush?.running || selectedSaved.size === 0 || !mcSession) return;
    const queue = savedRecipes.filter(r => selectedSaved.has(r.id));
    bulkCancelRef.current = false;
    setBulkPush({ running: true, total: queue.length, current: 0, currentTitle: '', failed: [] });

    const failed = [];
    for (let i = 0; i < queue.length; i++) {
      if (bulkCancelRef.current) break;
      const r = queue[i];
      setBulkPush(prev => ({ ...prev, current: i, currentTitle: r.title }));

      try {
        const raw = r.recipe || (typeof r.recipe_data === 'string' ? JSON.parse(r.recipe_data) : r.recipe_data);
        const { _mc_payload, ...recipe } = raw || {};
        let payload = (_mc_payload && _mc_payload.servingSizes) ? _mc_payload : null;
        if (!payload) {
          const rb = await aiAPI.rebuildMcPayload(recipe, lang).catch(() => null);
          payload = rb?.data?.mc_payload?.servingSizes ? rb.data.mc_payload : null;
        }
        if (!payload) { failed.push({ title: r.title, reason: t('saved.bulk_no_payload') }); continue; }

        // Zdjęcie wcześniej ściągnięte z MC (plik na dysku serwera, zob. mc-fetch-photo)
        // — wgraj świeże media na MC tuż przed wysyłką, tak jak przy ręcznym uploadzie
        let media = null;
        if (recipe._dish_photo_saved) {
          const photoRes = await aiAPI.mcImageFromSaved(r.id, lang).catch(() => null);
          if (photoRes?.data?.success) {
            media = { detailsMediaId: photoRes.data.detailsMediaId, thumbnailMediaId: photoRes.data.thumbnailMediaId };
          }
        }

        const job = await aiAPI.pushMcAsync(payload, lang, media);
        const jobId = job?.data?.job_id;
        if (!jobId) { failed.push({ title: r.title, reason: t('saved.bulk_no_payload') }); continue; }

        const until = Date.now() + 300000;
        let done = false, ok = false, url = null, recipeId = null;
        while (!done && Date.now() < until && !bulkCancelRef.current) {
          await new Promise(res => setTimeout(res, 2000));
          const st = (await aiAPI.mcPushStatus(jobId).catch(() => null))?.data;
          if (st?.status === 'done') { done = true; ok = true; url = st.url; recipeId = st.recipeId; }
          else if (st?.status === 'error') { done = true; }
        }
        if (ok) {
          await aiAPI.updateSavedRecipe(r.id, { mc_url: url, mc_recipe_id: String(recipeId || '') }).catch(() => {});
        } else {
          failed.push({ title: r.title, reason: t('saved.bulk_push_failed') });
        }
      } catch {
        failed.push({ title: r.title, reason: t('saved.bulk_push_failed') });
      }
    }

    setBulkPush({ running: false, total: queue.length, current: queue.length, currentTitle: '', failed });
    setSelectedSaved(new Set());
    loadSavedRecipes();
  };

  const cancelBulkPush = () => { bulkCancelRef.current = true; };

  // Wsadowe ściąganie zdjęć zaznaczonych przepisów z MC (przed skasowaniem ich tam).
  // Pomija przepisy bez mc_url (nic nie ma tam do pobrania).
  const handleBulkFetchPhotos = async () => {
    if (bulkPhoto?.running || selectedSaved.size === 0) return;
    const queue = savedRecipes.filter(r => selectedSaved.has(r.id) && r.mc_url);
    if (queue.length === 0) return;
    bulkPhotoCancelRef.current = false;
    setBulkPhoto({ running: true, total: queue.length, current: 0, currentTitle: '', failed: [] });

    const failed = [];
    for (let i = 0; i < queue.length; i++) {
      if (bulkPhotoCancelRef.current) break;
      const r = queue[i];
      setBulkPhoto(prev => ({ ...prev, current: i, currentTitle: r.title }));
      try {
        const res = await aiAPI.fetchMcPhoto(r.id, r.title, r.mc_recipe_id, lang);
        if (!res?.data?.found) failed.push({ title: r.title, reason: res?.data?.error || t('saved.bulk_no_payload') });
      } catch (err) {
        failed.push({ title: r.title, reason: err?.response?.data?.error || t('conv.error_net') });
      }
    }

    setBulkPhoto({ running: false, total: queue.length, current: queue.length, currentTitle: '', failed });
    loadSavedRecipes();
  };

  const cancelBulkPhoto = () => { bulkPhotoCancelRef.current = true; };

  const handleFetchPhoto = async (r, e) => {
    e.stopPropagation();
    setPhotoFetch(prev => ({ ...prev, [r.id]: 'loading' }));
    try {
      const res = await aiAPI.fetchMcPhoto(r.id, r.title, r.mc_recipe_id, lang);
      setPhotoFetch(prev => ({ ...prev, [r.id]: res?.data?.found ? 'done' : 'error' }));
    } catch {
      setPhotoFetch(prev => ({ ...prev, [r.id]: 'error' }));
    }
  };

  const resetAll = () => {
    setPrompt(''); setResult(null); setMcPayload(null); setEditing(false);
    setError(''); setPushSuccess(''); setSaveSuccess('');
    setSelectedSteps(new Set()); setFixNotes('');
    setCurrentSavedId(null); setPushedResult(null); setPushedUrl(null);
    clearDishImage();
  };

  const toggleStep = (idx) => {
    setSelectedSteps(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleFixSteps = async () => {
    if (!result || selectedSteps.size === 0) return;
    setFixing(true); setError('');
    try {
      const res = await mcAPI.fixSteps({
        recipe: result,
        fix_indices: [...selectedSteps],
        notes: fixNotes,
      });
      if (res.success && res.new_steps) {
        setMcPayload(null);
        setResult(prev => {
          const newSteps = [...prev.steps];
          res.new_steps.forEach((ns, i) => {
            const idx = res.fix_indices?.[i];
            if (idx != null && idx < newSteps.length) newSteps[idx] = ns;
          });
          return { ...prev, steps: newSteps };
        });
        setSelectedSteps(new Set());
        setFixNotes('');
      } else {
        setError(tErr(res.error) || t('conv.error_conv'));
      }
    } catch (err) {
      setError(tErr(err.message) || t('conv.error_net'));
    } finally { setFixing(false); }
  };

  const handleRepair = async (isAnswer = false) => {
    if (!result) return;
    const instruction = isAnswer ? repairAnswer : repairInstruction;
    if (!instruction.trim()) return;
    setRepairing(true); setError(''); setSaveSuccess('');
    try {
      // Historia: gdy odpowiadamy na pytanie AI, budujemy pełny dialog
      // [user: oryginalna instrukcja, assistant: pytanie, user: odpowiedź]
      // Backend oczekuje że history[0] to oryginalna instrukcja użytkownika
      const history = isAnswer
        ? [
            ...repairHistory,
            { role: 'assistant', content: repairQuestion },
          ]
        : [];
      const res = await mcAPI.repairRecipe({
        recipe: result,
        instruction: isAnswer ? repairAnswer : instruction,
        selected_steps: isAnswer ? [] : [...selectedSteps],
        history,
      });
      if (!res || typeof res !== 'object') {
        setError('Serwer nie odpowiedział poprawnie');
        return;
      }
      if (res.action === 'question') {
        setRepairQuestion(res.question || '?');
        if (!isAnswer) {
          setRepairHistory([{ role: 'user', content: instruction }]);
        }
        setRepairAnswer('');
      } else if (res.action === 'fix' && res.recipe) {
        setResult(res.recipe);
        setMcPayload(null);
        setRepairInstruction('');
        setRepairQuestion('');
        setRepairAnswer('');
        setRepairHistory([]);
        setSelectedSteps(new Set());
        setRepairSavePrompt(true);
      } else {
        setError(res.error || `Nieoczekiwana odpowiedź: ${JSON.stringify(res).slice(0, 100)}`);
      }
    } catch (err) {
      setError(err.message || t('conv.error_net'));
    } finally { setRepairing(false); }
  };

  const copyJSON = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    setCopiedJSON(true);
    setTimeout(() => setCopiedJSON(false), 2000);
  };

  const copyText = async () => {
    if (!result) return;
    const d = result;
    let txt = `${d.title}\n${'='.repeat((d.title || '').length || 10)}\n\n${t('conv.ingredients')}:\n`;
    txt += (d.ingredients || []).map(i => `• ${i.name}: ${i.quantity}`).join('\n');
    txt += `\n\n${t('conv.steps_title')}:\n` + (d.steps || []).map((s, i) => {
      let l = `${i + 1}. [${(s.type || 'MANUAL').toUpperCase()}] ${s.title}`;
      if (s.temperature) l += ` | ${s.temperature}°C`;
      if (s.speed != null) l += ` | ${t('conv.speed')} ${s.speed}`;
      if (s.time_seconds) l += ` | ${s.time_seconds >= 60 ? `${Math.floor(s.time_seconds / 60)} min ${s.time_seconds % 60}s` : `${s.time_seconds}s`}`;
      return l;
    }).join('\n');
    await navigator.clipboard.writeText(txt);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  const typeIcon = (type) => {
    switch (type) {
      case 'cook': return 'fas fa-fire';
      case 'mix': return 'fas fa-blender';
      case 'weigh': return 'fas fa-weight';
      default: return 'fas fa-hand-point-right';
    }
  };

  const cycleStepType = (idx) => {
    setMcPayload(null); setDirty(true);
    setResult(prev => {
      const steps = [...prev.steps];
      const types = ['manual', 'mix', 'cook', 'weigh'];
      const cur = types.indexOf(steps[idx].type);
      steps[idx] = { ...steps[idx], type: types[(cur + 1) % types.length] };
      return { ...prev, steps };
    });
  };

  const difficultyLabel = (d) => {
    if (d === 'hard') return t('conv.difficulty_hard');
    if (d === 'medium') return t('conv.difficulty_medium');
    return t('conv.difficulty_easy');
  };

  const fixStepsLabel = (n) => {
    if (n === 1) return t('conv.fix_step');
    if (n < 5) return t('conv.fix_steps_few');
    return t('conv.fix_steps_many');
  };

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Bramka połączenia z MC. Bez wtyczki (albo bez aktywnej sesji MC) wysyłka przepisu
  // nie ma jak zadziałać, a użytkownicy tego nie zauważali — dlatego modal + dźwięk.
  //   'no-ext'     — wtyczka nie odpowiada na ping (desktop)
  //   'no-session' — wtyczka/apka odpowiada, ale nie ma zalogowanego konta MC
  const connectNeed = !user || mcSession ? null
    : onMcConnect ? 'no-session'
    : isMobile ? null
    : extDetected ? 'no-session' : 'no-ext';
  const [connectGate, setConnectGate] = useState(null);
  const gateConvertShownRef = useRef(false);
  const openConnectGate = (sound = true) => {
    setConnectGate(connectNeed || 'no-ext');
    if (sound) playAlertSound();
  };
  // Handshake z wtyczką (App.jsx) trwa do kilku sekund — modal dopiero po tym oknie,
  // raz na kartę przeglądarki (sessionStorage), żeby odświeżenie strony nie nękało.
  useEffect(() => {
    if (!connectNeed) return;
    let seen = false;
    try { seen = !!sessionStorage.getItem('mc_gate_seen'); } catch {}
    if (seen) return;
    const timer = setTimeout(() => {
      try { sessionStorage.setItem('mc_gate_seen', '1'); } catch {}
      setConnectGate(connectNeed);
      playAlertSound();
    }, 8000);
    return () => clearTimeout(timer);
  }, [connectNeed]);
  // Sesja dotarła (wtyczka zainstalowana, konto MC zalogowane) — bramka niepotrzebna
  useEffect(() => { if (!connectNeed) setConnectGate(null); }, [connectNeed]);
  // Zmiana rodzaju problemu (np. wtyczka wykryta, ale bez sesji) — aktualizuj treść otwartego modala
  useEffect(() => { if (connectNeed) setConnectGate(g => (g ? connectNeed : g)); }, [connectNeed]);

  const dismissExtBanner = () => {
    localStorage.setItem('ext_banner_dismissed', '1');
    setExtBannerDismissed(true);
  };

  return (
    <div className="convert-v2">
      <div className="convert-tabs">
        <button
          className={`convert-tab ${activeTab === 'converter' ? 'convert-tab--active' : ''}`}
          onClick={() => setActiveTab('converter')}
        >
          <i className="fas fa-blender"></i>
          <span className="convert-tab-label--full">{t('conv.title')}</span>
          <span className="convert-tab-label--short">{t('conv.title_short')}</span>
        </button>
        <button
          className={`convert-tab ${activeTab === 'saved' ? 'convert-tab--active' : ''}`}
          onClick={() => setActiveTab('saved')}
        >
          <i className="fas fa-bookmark"></i>
          <span className="convert-tab-label--full">{t('saved.title')}</span>
          <span className="convert-tab-label--short">{t('saved.title_short')}</span>
          {savedRecipes.length > 0 && <span className="convert-tab-badge">{savedRecipes.length}</span>}
        </button>
      </div>

      {activeTab === 'saved' && (
        <div className="saved-tab-view">
          <div className="saved-pack-import-bar">
            <input
              ref={jsonPackImportInputRef}
              type="file"
              accept=".mtmeal,.json,application/json"
              style={{ display: 'none' }}
              onChange={e => { handleImportPackFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            <button className="btn btn-sm btn-outline" onClick={() => jsonPackImportInputRef.current?.click()}
              disabled={packBusy}>
              <i className={packBusy ? 'fas fa-spinner fa-spin' : 'fas fa-file-import'}></i> {t('saved.pack_import')}
            </button>
            {packMsg && <div className="convert-url-ok"><i className="fas fa-check"></i> {packMsg}</div>}
          </div>
          {savedRecipes.length === 0 ? (
            <div className="saved-empty"><i className="fas fa-bookmark"></i> {t('saved.empty')}</div>
          ) : (
            <>
              {mcSession && (
                <div className="saved-bulk-bar">
                  <label className="saved-bulk-select-all">
                    <input
                      type="checkbox"
                      checked={selectedSaved.size === savedRecipes.length}
                      onChange={toggleSelectAllSaved}
                      disabled={!!bulkPush?.running}
                    />
                    {t('saved.select_all')}
                  </label>
                  <button
                    className="btn btn-sm btn-outline"
                    disabled={selectedSaved.size === 0 || !!bulkPhoto?.running || !!bulkPush?.running}
                    onClick={handleBulkFetchPhotos}
                    title={t('saved.bulk_fetch_photos_hint')}
                  >
                    <i className="fas fa-camera"></i> {t('saved.bulk_fetch_photos')} ({selectedSaved.size})
                  </button>
                  {bulkPhoto?.running && (
                    <button className="btn btn-sm btn-outline" onClick={cancelBulkPhoto}>
                      {t('saved.bulk_cancel')}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline"
                    disabled={selectedSaved.size === 0 || packBusy}
                    onClick={handleExportPack}
                  >
                    <i className={packBusy ? 'fas fa-spinner fa-spin' : 'fas fa-file-export'}></i> {t('saved.pack_export')} ({selectedSaved.size})
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={selectedSaved.size === 0 || !!bulkPush?.running || !!bulkPhoto?.running}
                    onClick={handleBulkPush}
                  >
                    <i className="fas fa-paper-plane"></i> {t('saved.bulk_send')} ({selectedSaved.size})
                  </button>
                  {bulkPush?.running && (
                    <button className="btn btn-sm btn-outline" onClick={cancelBulkPush}>
                      {t('saved.bulk_cancel')}
                    </button>
                  )}
                </div>
              )}

              {bulkPhoto && (
                <div className="saved-bulk-progress">
                  <div className="saved-bulk-progress-bar">
                    <div
                      className="saved-bulk-progress-fill"
                      style={{ width: `${Math.round((bulkPhoto.current / Math.max(1, bulkPhoto.total)) * 100)}%` }}
                    />
                  </div>
                  <div className="saved-bulk-progress-label">
                    {bulkPhoto.running
                      ? `${t('saved.bulk_fetch_photos')}: ${bulkPhoto.current}/${bulkPhoto.total} — ${bulkPhoto.currentTitle} (${Math.round((bulkPhoto.current / bulkPhoto.total) * 100)}%)`
                      : `${t('saved.bulk_photos_done')}: ${bulkPhoto.total - bulkPhoto.failed.length}/${bulkPhoto.total} ${t('saved.bulk_photos_ok')}`}
                  </div>
                  {!bulkPhoto.running && bulkPhoto.failed.length > 0 && (
                    <ul className="saved-bulk-errors">
                      {bulkPhoto.failed.map((f, i) => (
                        <li key={i}>{f.title} — {f.reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {bulkPush && (
                <div className="saved-bulk-progress">
                  <div className="saved-bulk-progress-bar">
                    <div
                      className="saved-bulk-progress-fill"
                      style={{ width: `${Math.round((bulkPush.current / Math.max(1, bulkPush.total)) * 100)}%` }}
                    />
                  </div>
                  <div className="saved-bulk-progress-label">
                    {bulkPush.running
                      ? `${bulkPush.current}/${bulkPush.total} — ${bulkPush.currentTitle} (${Math.round((bulkPush.current / bulkPush.total) * 100)}%)`
                      : `${t('saved.bulk_done')}: ${bulkPush.total - bulkPush.failed.length}/${bulkPush.total} ${t('saved.bulk_ok')}`}
                  </div>
                  {!bulkPush.running && bulkPush.failed.length > 0 && (
                    <ul className="saved-bulk-errors">
                      {bulkPush.failed.map((f, i) => (
                        <li key={i}>{f.title} — {f.reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="saved-recipes-list">
                {savedRecipes.map(r => (
                  <div key={r.id} className="saved-recipe-card">
                    {mcSession && (
                      <input
                        type="checkbox"
                        className="saved-recipe-checkbox"
                        checked={selectedSaved.has(r.id)}
                        onChange={() => toggleSavedSelect(r.id)}
                        disabled={!!bulkPush?.running}
                      />
                    )}
                    <div className="saved-recipe-info">
                      <span className="saved-recipe-name">{r.title}</span>
                      <span className="saved-recipe-date">{new Date(r.created_at).toLocaleDateString()}</span>
                      {r.mc_url && (
                        <a className="saved-recipe-mc-link" href={r.mc_url} target="_blank" rel="noreferrer">
                          <i className="fas fa-external-link-alt"></i> MC
                        </a>
                      )}
                    </div>
                    <div className="saved-recipe-actions">
                      <button className="btn btn-xs btn-outline" onClick={() => setSavedPreview(r)}>
                        <i className="fas fa-eye"></i> {t('saved.preview')}
                      </button>
                      <button className="btn btn-xs btn-primary" onClick={() => handleLoadSaved(r)}>
                        <i className="fas fa-upload"></i> {t('saved.load')}
                      </button>
                      {r.mc_url && (
                        <button
                          className="btn btn-xs btn-outline"
                          disabled={photoFetch[r.id] === 'loading'}
                          onClick={(e) => handleFetchPhoto(r, e)}
                          title={t('saved.fetch_photo')}
                        >
                          <i className={`fas ${photoFetch[r.id] === 'loading' ? 'fa-spinner fa-spin' : 'fa-camera'}`}></i>
                          {photoFetch[r.id] === 'done' && <i className="fas fa-check saved-photo-ok"></i>}
                          {photoFetch[r.id] === 'error' && <i className="fas fa-times saved-photo-err"></i>}
                        </button>
                      )}
                      <button className="btn btn-xs btn-danger" onClick={(e) => handleDeleteSaved(r.id, e)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* W apce Android: zachęta do połączenia z MC od razu po zalogowaniu,
          zanim użytkownik skonwertuje pierwszy przepis (połączenie robi się raz) */}
      {user && !mcSession && onMcConnect && (
        <div className="ext-store-banner">
          <i className="fas fa-link ext-store-banner__icon"></i>
          <span className="ext-store-banner__text">{t('conv.mc_connect_note')}</span>
          <button className="btn btn-primary ext-store-banner__cta" onClick={onMcConnect}>
            <i className="fas fa-link"></i> {t('conv.mc_connect_btn')}
          </button>
        </div>
      )}

      {!mcSession && !isMobile && !extBannerDismissed && extBannerReady && (
        <div className="ext-store-banner">
          <i className="fas fa-puzzle-piece ext-store-banner__icon"></i>
          <span className="ext-store-banner__text">{t('ext.banner_text')}</span>
          <a
            className="btn btn-primary ext-store-banner__cta"
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noreferrer"
          >
            <i className="fab fa-chrome"></i> {t('ext.banner_cta')}
          </a>
          <button className="ext-store-banner__close" onClick={dismissExtBanner} aria-label="Zamknij">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {bgJob && (
        <div className={`convert-bgjob ${bgJob.status === 'done' ? 'convert-bgjob--done' : ''}`}>
          {bgJob.status === 'done' ? (
            <>
              <i className="fas fa-check-circle"></i>
              <span>{t('conv.bg_done')} <strong>{bgJob.title}</strong></span>
              <a className="btn btn-primary btn-xs" href={bgJob.url} target="_blank" rel="noreferrer">
                <i className="fas fa-external-link-alt"></i> {t('conv.open_mc')}
              </a>
            </>
          ) : (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              <span>{t('conv.bg_waiting')} <strong>{bgJob.title}</strong></span>
            </>
          )}
          <button className="convert-bgjob-x" onClick={() => setBgJob(null)} aria-label="X">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {activeTab === 'converter' && (
      <div className="convert-v2-inner">
        {/* LEFT */}
        <div className="convert-v2-left">
          <div className="convert-v2-header">
            <h2><i className="fas fa-blender"></i> {t('conv.title')}</h2>
            <p className="convert-v2-sub">{t('conv.sub')}</p>
          </div>

          <div className="mc-session-bar">
            {mcSession ? (
              <div className="mc-session-connected">
                <span><i className="fas fa-check-circle"></i> {t('conv.mc_connected')}</span>
                <select
                  className="mc-locale-select"
                  value={lang}
                  onChange={e => changeLang(e.target.value)}
                  title={t('conv.mc_locale_title')}
                >
                  <option value="pl">🇵🇱 PL</option>
                  <option value="en">🇬🇧 EN</option>
                  <option value="de">🇩🇪 DE</option>
                </select>
              </div>
            ) : isMobile ? (
              <div className="mc-session-disconnected">
                <span><i className="fas fa-mobile-alt"></i> {t('conv.mobile_no_session')}</span>
              </div>
            ) : extDetected && !onMcConnect ? (
              // Wtyczka działa, ale konto MC niezalogowane — inny problem niż brak wtyczki
              <div className="mc-session-disconnected mc-session-disconnected--warn">
                <span><i className="fas fa-user-lock"></i> {t('gate.nosess_bar')} — <button className="ext-install-link" onClick={() => openConnectGate(false)}>{t('gate.nosess_bar_link')}</button></span>
              </div>
            ) : (
              <div className="mc-session-disconnected">
                <span><i className="fas fa-plug"></i> {t('conv.install_ext')} — <button className="ext-install-link" onClick={() => setShowExtModal(true)}>{t('ext.title').split(' ')[0]}</button></span>
              </div>
            )}
          </div>

          <div className="convert-prompt-stage">
            <div className="convert-field">
              <label className="convert-label">{t('conv.url_label')}</label>
              <div className="convert-url-row">
                <input
                  className="convert-input"
                  type="url"
                  value={importUrl}
                  onChange={e => setImportUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleImportUrl(); }}
                  placeholder={t('conv.url_placeholder')}
                  disabled={importing || converting}
                />
                <button className="btn btn-secondary" onClick={handleImportUrl}
                  disabled={importing || converting || !importUrl.trim()}>
                  {importing
                    ? <><i className="fas fa-spinner fa-spin"></i> {t('conv.url_loading')}</>
                    : <><i className="fas fa-link"></i> {t('conv.url_btn')}</>}
                </button>
              </div>
              {importMsg && <div className="convert-url-ok"><i className="fas fa-check"></i> {importMsg}</div>}
            </div>

            <div className="convert-field">
              <label className="convert-label">{t('conv.image_label')}</label>
              <input
                ref={ocrInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => { handleOcrFile(e.target.files?.[0]); e.target.value = ''; }}
              />
              <button className="btn btn-secondary" onClick={() => ocrInputRef.current?.click()}
                disabled={ocrLoading || converting}>
                {ocrLoading
                  ? <><i className="fas fa-spinner fa-spin"></i> {t('conv.image_loading')}</>
                  : <><i className="fas fa-image"></i> {t('conv.image_btn')}</>}
              </button>
              {isNativeApp() && (
                <>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={e => { handleOcrFile(e.target.files?.[0]); e.target.value = ''; }}
                  />
                  <button className="btn btn-secondary" onClick={() => cameraInputRef.current?.click()}
                    disabled={ocrLoading || converting} style={{ marginTop: '8px' }}>
                    <i className="fas fa-camera"></i> {t('conv.image_camera')}
                  </button>
                </>
              )}
              <div className="convert-hint">{t('conv.image_hint')}</div>
            </div>

            <div className="convert-field">
              <label className="convert-label">{t('conv.json_import_label')}</label>
              <input
                ref={jsonImportInputRef}
                type="file"
                accept=".mtmeal,.json,application/json"
                style={{ display: 'none' }}
                onChange={e => { handleImportJSONFile(e.target.files?.[0]); e.target.value = ''; }}
              />
              <button className="btn btn-secondary" onClick={() => jsonImportInputRef.current?.click()}
                disabled={converting}>
                <i className="fas fa-file-import"></i> {t('conv.import_json')}
              </button>
              <div className="convert-hint">{t('conv.json_import_hint')}</div>
            </div>

            <div className="convert-field">
              <label className="convert-label">{t('conv.paste_label')}</label>
              <textarea
                className="convert-textarea convert-textarea--lg"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onPaste={handleImagePaste}
                placeholder={t('conv.paste_placeholder')}
                rows={10}
                disabled={converting}
              />
            </div>

            {usage && !usage.unlimited && (
              <div className={`conversion-usage ${usage.remaining === 0 ? 'conversion-usage-full' : usage.remaining <= 2 ? 'conversion-usage-warn' : ''}`}>
                <div className="conversion-usage-bar-wrap">
                  <div className="conversion-usage-bar" style={{ width: `${Math.min(100, Math.round((usage.count / usage.limit) * 100))}%` }} />
                </div>
                <span className="conversion-usage-text">
                  {usage.remaining === 0
                    ? <><i className="fas fa-lock"></i> {t('conv.limit_exhausted')} ({usage.count}/{usage.limit}){usage.reset_at ? ` — ${t('conv.limit_renews')} ${new Date(usage.reset_at).toLocaleDateString()}` : ''}</>
                    : <><i className="fas fa-chart-bar"></i> {usage.count}/{usage.limit} {t('conv.limit_usage')} ({usage.remaining} {t('conv.limit_left')})</>
                  }
                </span>
              </div>
            )}

            <button className="btn btn-primary convert-btn" onClick={() => handleConvert()}
              disabled={converting || (usage && !usage.unlimited && usage.remaining === 0)}>
              {converting
                ? <><i className="fas fa-spinner fa-spin"></i> {t('conv.converting')}</>
                : <><i className="fas fa-robot"></i> {t('conv.convert_btn')}</>}
            </button>

            {converting && (
              <div className="convert-progress-wrap">
                <div className="convert-progress-bar" style={{ width: `${progress}%` }} />
                <span className="convert-progress-pct">{progress}%</span>
                {stageMsg && <span className="convert-progress-stage">{stageMsg}</span>}
              </div>
            )}

            {converting && (
              <div className={`convert-fun-msg ${funMsgVisible ? 'convert-fun-msg--in' : 'convert-fun-msg--out'}`}>
                <i className="fas fa-hat-chef"></i> {convertingMsgs[funMsgIdx]}
              </div>
            )}

            {result && !converting && (
              <button className="convert-link" onClick={resetAll} style={{ textAlign: 'center', display: 'block', marginTop: 6 }}>
                <i className="fas fa-undo"></i> {t('conv.reset')}
              </button>
            )}

            <div className="convert-warning">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{t('conv.warning')}</span>
            </div>

            {error && <div className="mc-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          </div>

          {saveSuccess && <div className="convert-push-ok"><i className="fas fa-bookmark"></i> {saveSuccess}</div>}
        </div>

        {/* RIGHT */}
        <div className="convert-v2-right" ref={resultRef}>
          {!result ? (
            <div className="convert-empty">
              <i className="fas fa-robot"></i>
              <p>{t('conv.empty_hint1')}<br /><strong>{t('conv.empty_hint2')}</strong></p>
              <p className="convert-empty-hint">{t('conv.empty_hint3')}</p>
              {!mcSession && (
                <div className="convert-empty-mc-note">
                  <i className="fas fa-info-circle"></i>
                  {/* W apce Android sesję MC podpina natywne logowanie, nie wtyczka */}
                  <span>{onMcConnect ? t('conv.mc_connect_note') : t('conv.empty_mc_note')}</span>
                </div>
              )}
              {!mcSession && onMcConnect && (
                <button className="btn btn-primary btn-xs" onClick={onMcConnect}>
                  <i className="fas fa-link"></i> {t('conv.mc_connect_btn')}
                </button>
              )}
            </div>
          ) : (
            <div className={`convert-result ${editing ? 'is-editing' : ''}`}>
              <div className="convert-result-header">
                {editing ? (
                  <input className="convert-edit-title" value={result.title || ''}
                    onChange={e => updateField('title', e.target.value)} />
                ) : (
                  <h3>{result.title || t('conv.recipe_default')}</h3>
                )}
                <div className="convert-result-meta-tags">
                  {result.category && (
                    <span className="convert-tag convert-tag--category">
                      <i className="fas fa-tag"></i> {result.category}
                    </span>
                  )}
                  {result.complexity && (
                    <span className={`convert-tag convert-tag--${result.complexity}`}>
                      <i className="fas fa-signal"></i> {difficultyLabel(result.complexity)}
                    </span>
                  )}
                </div>
                {warnings && warnings.length > 0 && (
                  <div className="convert-warnings">
                    {warnings.map((w, i) => <div key={i}><i className="fas fa-exclamation-circle"></i> {w}</div>)}
                  </div>
                )}
                {audit && audit.fixed.length > 0 && (
                  <div className="convert-audit">
                    <button type="button" className="convert-audit-head"
                      onClick={() => setAuditOpen(o => !o)}
                      aria-expanded={auditOpen}>
                      <i className="fas fa-shield-halved"></i>
                      <span>{t('conv.audit_fixed').replace('{n}', audit.fixed.length)}</span>
                      <i className={`fas fa-chevron-${auditOpen ? 'up' : 'down'} convert-audit-caret`}></i>
                    </button>
                    {auditOpen && (
                      <ul className="convert-audit-list">
                        {audit.fixed.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                <div className="convert-result-actions">
                  <button className="btn btn-secondary btn-xs" onClick={() => { setEditing(!editing); setDirty(false); }}>
                    <i className={`fas ${editing ? 'fa-check' : 'fa-edit'}`}></i> {editing ? t('conv.save_changes') : t('conv.edit')}
                  </button>
                  {user?.is_admin && <>
                    <button className={`btn btn-secondary btn-xs ${copiedJSON ? 'btn-copied' : ''}`} onClick={copyJSON}>
                      <i className={copiedJSON ? 'fas fa-check' : 'fas fa-copy'}></i> JSON
                    </button>
                    <button className={`btn btn-secondary btn-xs ${copiedText ? 'btn-copied' : ''}`} onClick={copyText}>
                      <i className={copiedText ? 'fas fa-check' : 'fas fa-clipboard'}></i> {t('saved.preview').split('')[0] === 'P' ? 'Tekst' : 'Text'}
                    </button>
                  </>}
                  <button className="btn btn-secondary btn-xs" onClick={() => handleSave()} disabled={saving}>
                    <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-bookmark'}></i> {t('conv.save')}
                  </button>
                  <button className="btn btn-secondary btn-xs" onClick={exportRecipeJSON}>
                    <i className="fas fa-file-export"></i> {t('conv.export_json')}
                  </button>
                  {!mcSession && onMcConnect && (
                    <button className="btn btn-primary btn-xs" onClick={onMcConnect}>
                      <i className="fas fa-link"></i> {t('conv.mc_connect_btn')}
                    </button>
                  )}
                  {mcSession && (result === pushedResult && pushedUrl ? (
                    // Po wysłaniu przycisk wysyłki zamienia się w link do przepisu na MC
                    <a className="btn btn-primary btn-xs btn-open-mc" href={pushedUrl} target="_blank" rel="noreferrer">
                      <i className="fas fa-external-link-alt"></i> {t('conv.open_mc')}
                    </a>
                  ) : (
                    <button
                      className={`btn btn-primary btn-xs btn-push-mc ${!pushing && result !== pushedResult ? 'btn-push-mc--pulse' : ''}`}
                      onClick={handlePush}
                      disabled={pushing || result === pushedResult}
                    >
                      <i className={pushing ? 'fas fa-spinner fa-spin' : result === pushedResult ? 'fas fa-check' : 'fas fa-upload'}></i>{' '}
                      {pushChecking ? t('conv.checking_mc_btn') : pushing ? t('conv.sending_mc') : result === pushedResult ? t('conv.sent_mc') : t('conv.send_mc')}
                    </button>
                  ))}
                </div>
                {pushChecking && (
                  <div className="convert-push-ok convert-push-ok--result">
                    <i className="fas fa-spinner fa-spin"></i>{' '}
                    {t(pushChecking === 'server' ? 'conv.checking_srv' : 'conv.checking_mc')}
                  </div>
                )}
                {pushSuccess && !pushChecking && (
                  <div className="convert-push-ok convert-push-ok--result">
                    <i className="fas fa-check-circle"></i> {pushSuccess}
                  </div>
                )}
              </div>

              {mcSession && (
                <div className="convert-dish">
                  <input
                    type="file"
                    ref={dishInputRef}
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleDishFile(e.target.files?.[0])}
                  />
                  {dishPreview ? (
                    <div className="convert-dish-set">
                      <img className="convert-dish-thumb" src={dishPreview} alt="" />
                      <span className="convert-dish-ok"><i className="fas fa-check-circle"></i> {t('conv.dish_ok')}</span>
                      <button className="btn btn-secondary btn-xs" onClick={clearDishImage}>
                        <i className="fas fa-times"></i> {t('conv.dish_remove')}
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-secondary btn-xs"
                      onClick={() => dishInputRef.current?.click()}
                      disabled={dishUploading}
                    >
                      <i className={dishUploading ? 'fas fa-spinner fa-spin' : 'fas fa-camera'}></i>{' '}
                      {dishUploading ? t('conv.dish_loading') : t('conv.dish_btn')}
                    </button>
                  )}
                  <span className="convert-dish-hint">{dishError || t('conv.dish_hint')}</span>
                </div>
              )}

              {mcSession && (
                <div className="convert-safety"><i className="fas fa-shield-alt"></i> {t('conv.safety')}</div>
              )}

              <div className="convert-result-meta">
                {result.servings && <span><i className="fas fa-users"></i> {result.servings} {t('conv.servings')}</span>}
                {result.total_time_minutes && <span><i className="far fa-clock"></i> {result.total_time_minutes} min</span>}
                <span><i className="fas fa-robot"></i> {result.steps?.length || 0} {t('conv.steps_count')}</span>
              </div>

              {result.ingredients?.length > 0 && (
                <div className="convert-ing-block">
                  <h4>{t('conv.ingredients')} {editing && <button className="btn btn-xs btn-secondary" onClick={addIngredient} style={{marginLeft:8}}>{t('conv.add')}</button>}</h4>
                  <ul>{result.ingredients.map((ing, i) => (
                    <li key={i}>
                      {editing ? (
                        <>
                          <input className="convert-edit-ing-name" value={ing.name || ''}
                            onChange={e => updateIngredient(i, 'name', e.target.value)} placeholder={t('conv.ing_name_ph')} />
                          <input className="convert-edit-ing-qty" value={ing.quantity || ''}
                            onChange={e => updateIngredient(i, 'quantity', e.target.value)} placeholder={t('conv.ing_qty_ph')} />
                          <button className="convert-edit-remove" onClick={() => removeIngredient(i)} title={t('delete')}>
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <span>{ing.name}</span>
                          <span className="convert-ing-qty">{ing.quantity}</span>
                        </>
                      )}
                    </li>
                  ))}</ul>
                </div>
              )}

              <h4>{t('conv.steps_title')} {editing && <button className="btn btn-xs btn-secondary" onClick={addStep} style={{marginLeft:8}}>{t('conv.add')}</button>}</h4>
              {result.steps?.map((step, i) => {
                const typeCls = step.type === 'cook' ? 'cook' : step.type === 'mix' ? 'mix' : step.type === 'weigh' ? 'weigh' : 'manual';
                return (
                  <div key={i} className={`convert-step convert-step--${typeCls}`}>
                    {editing ? (
                      <div className="convert-step-edit">
                        <div className="convert-step-edit-top">
                          <button className="convert-edit-type" onClick={() => cycleStepType(i)} title={t('conv.edit')}>
                            <i className={typeIcon(step.type)}></i> {step.type}
                          </button>
                          <input className="convert-edit-title" value={step.title || ''}
                            onChange={e => updateStep(i, 'title', e.target.value)} placeholder={t('conv.step_title_ph')} />
                          <div className="convert-step-edit-arrows">
                            <button onClick={() => moveStep(i, -1)} disabled={i === 0}><i className="fas fa-chevron-up"></i></button>
                            <button onClick={() => moveStep(i, 1)} disabled={i === result.steps.length - 1}><i className="fas fa-chevron-down"></i></button>
                            <button className="convert-edit-remove" onClick={() => removeStep(i)}><i className="fas fa-times"></i></button>
                          </div>
                        </div>
                        <textarea className="convert-edit-desc" value={step.description || ''}
                          onChange={e => updateStep(i, 'description', e.target.value)} placeholder={t('conv.step_desc_ph')} rows={2} />
                        {(step.type === 'cook' || step.type === 'mix') && (
                          <div className="convert-edit-params">
                            <label>{t('conv.temp')} <input type="number" value={step.temperature || ''}
                              onChange={e => updateStep(i, 'temperature', e.target.value ? parseInt(e.target.value) : null)} placeholder="°C" /></label>
                            <label>{t('conv.speed')} <input type="number" value={step.speed != null ? step.speed : ''}
                              onChange={e => updateStep(i, 'speed', e.target.value ? parseInt(e.target.value) : null)} placeholder="1-10" /></label>
                            <label>{t('conv.time')} <input type="number" value={step.time_seconds || ''}
                              onChange={e => updateStep(i, 'time_seconds', e.target.value ? parseInt(e.target.value) : null)} placeholder="s" /></label>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="convert-step-head">
                          <label className="convert-step-check" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={selectedSteps.has(i)} onChange={() => toggleStep(i)} />
                          </label>
                          <span className="convert-step-num">{i + 1}</span>
                          <span className="convert-step-title">{step.title || step.description}</span>
                        </div>
                        {step.description && step.description !== step.title && (
                          <div className="convert-step-desc">{step.description}</div>
                        )}
                        <div className="convert-step-params">
                          {step.type === 'weigh' && <span className="convert-pill convert-pill--weigh"><i className="fas fa-weight"></i> {t('conv.step_weigh')}{step.weight ? ` · ${step.weight} g` : ''}</span>}
                          {step.type === 'manual' && <span className="convert-pill convert-pill--manual"><i className="fas fa-hand-paper"></i> {t('conv.step_manual')}</span>}
                          {step.temperature && <span className="convert-pill convert-pill--temp"><i className="fas fa-thermometer-half"></i> {step.temperature}°C</span>}
                          {step.speed != null && <span className="convert-pill convert-pill--speed"><i className="fas fa-tachometer-alt"></i> {t('conv.speed')} {step.speed}</span>}
                          {step.time_seconds && (
                            <span className="convert-pill convert-pill--time">
                              <i className="far fa-clock"></i> {step.time_seconds >= 60 ? `${Math.floor(step.time_seconds / 60)}m ${step.time_seconds % 60 > 0 ? step.time_seconds % 60 + 's' : ''}`.trim() : `${step.time_seconds}s`}
                            </span>
                          )}
                          {step.direction === 'clockwise' && <span className="convert-pill convert-pill--dir"><i className="fas fa-redo"></i> w prawo</span>}
                          {step.direction === 'counter_clockwise' && <span className="convert-pill convert-pill--dir-rev"><i className="fas fa-undo"></i> reverse</span>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              <div className="convert-repair-bar">
                {repairSavePrompt ? (
                  <div className="convert-repair-save-prompt">
                    <span><i className="fas fa-check-circle" style={{color:'var(--success)'}}></i> {t('conv.repair_ok')} {t('conv.repair_save_ask')}</span>
                    <div style={{display:'flex',gap:'6px',marginTop:6}}>
                      <button className="btn btn-primary btn-xs" onClick={() => {
                        handleSave(null, undefined, currentSavedId);
                        setRepairSavePrompt(false);
                      }} disabled={saving}>
                        <i className="fas fa-bookmark"></i> {t('conv.repair_save_yes')}
                      </button>
                      <button className="btn btn-ghost btn-xs" onClick={() => setRepairSavePrompt(false)}>
                        {t('conv.repair_save_no')}
                      </button>
                    </div>
                  </div>
                ) : repairQuestion ? (
                  <>
                    <div className="convert-repair-question">
                      <i className="fas fa-question-circle"></i> {repairQuestion}
                    </div>
                    <textarea
                      className="convert-input"
                      rows={2}
                      value={repairAnswer}
                      onChange={e => setRepairAnswer(e.target.value)}
                      placeholder={t('conv.repair_answer_placeholder')}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleRepair(true); } }}
                    />
                    <div style={{display:'flex',gap:'6px'}}>
                      <button className="btn btn-primary btn-xs" onClick={() => handleRepair(true)} disabled={repairing || !repairAnswer.trim()}>
                        {repairing ? <><i className="fas fa-spinner fa-spin"></i> {t('conv.repairing')}</> : <><i className="fas fa-paper-plane"></i> {t('conv.repair_send')}</>}
                      </button>
                      <button className="btn btn-ghost btn-xs" onClick={() => { setRepairQuestion(''); setRepairHistory([]); setRepairAnswer(''); }}>
                        {t('conv.repair_cancel')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="convert-repair-hint">
                      <i className="fas fa-info-circle"></i> {t('conv.repair_hint')}
                    </div>
                    <textarea
                      className="convert-input"
                      rows={2}
                      value={repairInstruction}
                      onChange={e => setRepairInstruction(e.target.value)}
                      placeholder={t('conv.repair_placeholder')}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleRepair(false); } }}
                    />
                    <button className="btn btn-secondary btn-xs" onClick={() => handleRepair(false)} disabled={repairing || !repairInstruction.trim()}>
                      {repairing ? <><i className="fas fa-spinner fa-spin"></i> {t('conv.repairing')}</> : <><i className="fas fa-magic"></i> {t('conv.repair_btn')}</>}
                    </button>
                  </>
                ) }
              </div>

              {result.nutrients && (
                <div className="convert-nutrients">
                  <h4>{t('conv.nutrients')}</h4>
                  <div className="convert-nutrients-grid">
                    {result.nutrients.kcal && <div className="convert-nutrient"><span className="convert-nutrient-val">{result.nutrients.kcal}</span><span className="convert-nutrient-label">kcal</span></div>}
                    {result.nutrients.protein && <div className="convert-nutrient"><span className="convert-nutrient-val">{result.nutrients.protein}g</span><span className="convert-nutrient-label">{t('conv.protein')}</span></div>}
                    {result.nutrients.carbs && <div className="convert-nutrient"><span className="convert-nutrient-val">{result.nutrients.carbs}g</span><span className="convert-nutrient-label">{t('conv.carbs')}</span></div>}
                    {result.nutrients.fat && <div className="convert-nutrient"><span className="convert-nutrient-val">{result.nutrients.fat}g</span><span className="convert-nutrient-label">{t('conv.fat')}</span></div>}
                  </div>
                </div>
              )}

              {result.tips && (
                <div className="convert-tips">
                  {editing ? (
                    <textarea className="convert-edit-desc" value={result.tips || ''}
                      onChange={e => updateField('tips', e.target.value)} rows={2} />
                  ) : (
                    <><div className="convert-tips-header"><i className="fas fa-lightbulb"></i> <strong>{t('conv.tips')}</strong></div><span>{result.tips}</span></>
                  )}
                </div>
              )}

              {editing && (
                <div className="convert-save-bar" role="region" aria-label={t('conv.editing')}>
                  <span className="convert-save-bar-label">
                    <i className="fas fa-pen"></i> {t('conv.editing')}
                    {dirty && <span className="convert-save-bar-dirty"> · {t('conv.unsaved')}</span>}
                  </span>
                  <button
                    className={`btn btn-primary btn-sm convert-save-bar-btn ${dirty ? 'convert-save-bar-btn--pulse' : ''}`}
                    onClick={() => { setEditing(false); setDirty(false); }}>
                    <i className="fas fa-check"></i> {t('conv.save_changes')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Saved recipe preview modal */}
      {savedPreview && (() => {
        let recipe = null;
        try { recipe = savedPreview.recipe || (typeof savedPreview.recipe_data === 'string' ? JSON.parse(savedPreview.recipe_data) : savedPreview.recipe_data); } catch {}
        return (
          <div className="modal-overlay" onClick={() => setSavedPreview(null)}>
            <div className="modal saved-preview-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2><i className="fas fa-eye"></i> {savedPreview.title}</h2>
                <button className="modal-close-btn" onClick={() => setSavedPreview(null)}><i className="fas fa-times"></i></button>
              </div>
              <div className="modal-body">
                {recipe ? (
                  <>
                    {previewPhotoUrl && (
                      <img className="saved-preview-photo" src={previewPhotoUrl} alt={savedPreview.title} />
                    )}
                    <div className="saved-preview-meta">
                      {recipe.servings && <span><i className="fas fa-users"></i> {recipe.servings} {t('conv.servings')}</span>}
                      {recipe.total_time_minutes && <span><i className="far fa-clock"></i> {recipe.total_time_minutes} min</span>}
                      {recipe.category && <span><i className="fas fa-tag"></i> {recipe.category}</span>}
                    </div>
                    {recipe.ingredients?.length > 0 && (
                      <>
                        <h4>{t('conv.ingredients')}</h4>
                        <ul className="saved-preview-ingredients">
                          {recipe.ingredients.map((ing, i) => (
                            <li key={i}><strong>{ing.quantity}</strong> {ing.name}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {recipe.steps?.length > 0 && (
                      <>
                        <h4>{t('conv.steps_title')}</h4>
                        <ol className="saved-preview-steps">
                          {recipe.steps.map((s, i) => (
                            <li key={i}><strong>{s.title}</strong> — {s.description}</li>
                          ))}
                        </ol>
                      </>
                    )}
                    {recipe.tips && <p className="saved-preview-tips"><i className="fas fa-lightbulb"></i> {recipe.tips}</p>}
                  </>
                ) : <p>{t('saved.no_data')}</p>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => handleLoadSaved(savedPreview)}>
                  <i className="fas fa-upload"></i> {t('saved.load_to_conv')}
                </button>
                {savedPreview.mc_url && (
                  <a className="btn btn-outline" href={savedPreview.mc_url} target="_blank" rel="noreferrer">
                    <i className="fas fa-external-link-alt"></i> {t('saved.open_mc')}
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bramka połączenia z MC: brak wtyczki albo brak aktywnej sesji Monsieur Cuisine */}
      {connectGate && (
        <div className="modal-overlay" onClick={() => setConnectGate(null)}>
          <div className="modal ext-modal connect-gate" role="alertdialog" aria-modal="true"
            aria-labelledby="connect-gate-title" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="connect-gate-title">
                <i className={`fas ${connectGate === 'no-ext' ? 'fa-puzzle-piece' : 'fa-user-lock'}`}></i>{' '}
                {t(connectGate === 'no-ext' ? 'gate.noext_title' : 'gate.nosess_title')}
              </h2>
              <button className="modal-close-btn" onClick={() => setConnectGate(null)} aria-label="Zamknij">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p className="connect-gate__lead">
                {t(connectGate === 'no-ext' ? 'gate.noext_lead' : 'gate.nosess_lead')}
              </p>
              {connectGate === 'no-ext' ? (
                <ol className="ext-steps">
                  <li>{t('ext.step1')}</li>
                  <li>{t('ext.step2')}</li>
                  <li>{t('ext.step3')}</li>
                </ol>
              ) : !onMcConnect && (
                <ol className="ext-steps">
                  <li>{t('gate.nosess_step1')}</li>
                  <li>{t('gate.nosess_step2')}</li>
                </ol>
              )}
              <div className="connect-gate__actions">
                {connectGate === 'no-ext' ? (
                  <a className="btn btn-primary" href={CHROME_STORE_URL} target="_blank" rel="noreferrer">
                    <i className="fab fa-chrome"></i> {t('ext.banner_cta')}
                  </a>
                ) : onMcConnect ? (
                  <button className="btn btn-primary" onClick={() => { setConnectGate(null); onMcConnect(); }}>
                    <i className="fas fa-link"></i> {t('conv.mc_connect_btn')}
                  </button>
                ) : (
                  <a className="btn btn-primary" href={`https://www.monsieur-cuisine.com/${lang}/`}
                    target="_blank" rel="noreferrer">
                    <i className="fas fa-external-link-alt"></i> {t('gate.nosess_cta')}
                  </a>
                )}
                {prompt.trim() && !converting && (
                  <button className="btn btn-secondary"
                    onClick={() => { setConnectGate(null); handleConvert({ skipGate: true }); }}>
                    {t('gate.convert_anyway')}
                  </button>
                )}
                <button className="btn btn-secondary" onClick={() => setConnectGate(null)}>
                  {t('gate.later')}
                </button>
              </div>
              <div className="ext-note">
                <i className="fas fa-info-circle"></i>
                <span>{t('gate.footnote')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extension install modal */}
      {showExtModal && (
        <div className="modal-overlay" onClick={() => setShowExtModal(false)}>
          <div className="modal ext-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-puzzle-piece"></i> {t('ext.title')}</h2>
              <button className="modal-close-btn" onClick={() => setShowExtModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <ol className="ext-steps">
                <li>{t('ext.step1')}</li>
                <li>{t('ext.step2')}</li>
                <li>{t('ext.step3')}</li>
              </ol>
              <div style={{textAlign: 'center', margin: '1.2rem 0'}}>
                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary"
                  style={{display: 'inline-flex', alignItems: 'center', gap: '0.5rem'}}
                  onClick={() => setShowExtModal(false)}
                >
                  <i className="fab fa-chrome"></i> {t('ext.banner_cta')}
                </a>
              </div>
              <div className="ext-note">
                <i className="fas fa-shield-alt"></i>
                <span>{t('ext.security')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie banner */}
      {cookieBanner && (
        <div className="cookie-banner">
          <span><i className="fas fa-cookie-bite"></i> {t('cookie.text')}</span>
          <button onClick={() => { localStorage.setItem('cookie-accepted', '1'); setCookieBanner(false); }}>{t('cookie.accept')}</button>
        </div>
      )}

      {/* Notka prawnoautorska (dyrektywa 2001/29/WE) — po zamknięciu notki cookies, żeby bannery nie nachodziły */}
      {!cookieBanner && copyrightBanner && (
        <div className="cookie-banner cookie-banner--copyright">
          <span><i className="fas fa-balance-scale"></i> {t('copyright.text')}</span>
          <button onClick={() => { localStorage.setItem('copyright-accepted', '1'); setCopyrightBanner(false); }}>{t('copyright.accept')}</button>
        </div>
      )}
    </div>
  );
}

export default RecipeConverter;
